import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import {
  Alert,
  Button,
  Card,
  Flex,
  List,
  Skeleton,
  Table,
  Tag,
  Typography,
  theme,
} from '@/shared/antd-imports';
import { CalendarOutlined, CreditCardOutlined, WalletOutlined } from '@ant-design/icons';
import { useGetProjectPaymentScheduleQuery, useStartInstallmentCheckoutMutation } from '@/store/api';
import { useResponsive } from '@/hooks/useResponsive';
import { ClientInstallmentStatus, ClientPaymentInstallment } from '@/types/payment-schedule';
import { formatCalendarDate, formatMinor, sumSameCurrency } from '@/utils/money';

interface PaymentScheduleCardProps {
  projectId: string;
}

/**
 * R2-F (ANS-109): the project's payment schedule as the client sees it (name, amount, due date, status, total).
 * Renders nothing when the provider has not turned it on, or when there are no payments yet. Never shows fees.
 */
export const PaymentScheduleCard: React.FC<PaymentScheduleCardProps> = ({ projectId }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useResponsive();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectPaymentScheduleQuery(projectId);
  const { message } = App.useApp();
  // ANS-45: "Pay now" opens the provider's hosted checkout (only on entries the server marked payable).
  const [startCheckout] = useStartInstallmentCheckoutMutation();
  const [payingId, setPayingId] = useState<string | null>(null);
  const handlePay = async (installmentId: string) => {
    setPayingId(installmentId);
    try {
      const result = await startCheckout({ projectId, installmentId }).unwrap();
      if (result.body?.url) {
        window.location.assign(result.body.url);
        return;
      }
      message.error(t('paymentSchedule.payError', { defaultValue: 'Could not start the payment. Please try again.' }));
    } catch (error) {
      const serverMessage = (error as { data?: { message?: unknown } })?.data?.message;
      message.error(
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : t('paymentSchedule.payError', { defaultValue: 'Could not start the payment. Please try again.' })
      );
    }
    setPayingId(null);
  };

  const title = (
    <Flex gap={8} align="center">
      <WalletOutlined />
      {t('paymentSchedule.title', { defaultValue: 'Payment schedule' })}
    </Flex>
  );

  const schedule = data?.body;
  const rows = useMemo(
    () => (schedule?.installments ?? []).map((row, index) => ({ ...row, key: `${index}-${row.name}` })),
    [schedule]
  );
  const currency = schedule?.currency ?? rows[0]?.currency ?? null;
  const total = currency ? sumSameCurrency(rows, currency) : null;

  if (isLoading) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Alert
          type="error"
          showIcon
          message={t('paymentSchedule.loadError', { defaultValue: 'The payment schedule could not be loaded.' })}
          action={
            <Button size="small" onClick={() => refetch()} loading={isFetching}>
              {t('paymentSchedule.retry', { defaultValue: 'Try again' })}
            </Button>
          }
        />
      </Card>
    );
  }

  if (!schedule?.enabled || rows.length === 0) return null;

  const statusLabel: Record<ClientInstallmentStatus, string> = {
    upcoming: t('paymentSchedule.status.upcoming', { defaultValue: 'Upcoming' }),
    due: t('paymentSchedule.status.due', { defaultValue: 'Due' }),
    overdue: t('paymentSchedule.status.overdue', { defaultValue: 'Overdue' }),
    partly_paid: t('paymentSchedule.status.partly_paid', { defaultValue: 'Partly paid' }),
    paid: t('paymentSchedule.status.paid', { defaultValue: 'Paid' }),
  };

  const renderStatus = (status: ClientInstallmentStatus) => (
    <Tag color={STATUS_COLOR[status] ?? 'default'} style={{ marginInlineEnd: 0 }}>
      {statusLabel[status] ?? status}
    </Tag>
  );
  const renderAmount = (row: ClientPaymentInstallment) =>
    row.amount_minor === null ? (
      <Typography.Text type="secondary">{t('paymentSchedule.amountHidden', { defaultValue: '—' })}</Typography.Text>
    ) : (
      <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatMinor(row.amount_minor, row.currency, i18n.language)}
      </Typography.Text>
    );
  const renderPay = (row: ClientPaymentInstallment) =>
    row.pay_online && row.id ? (
      <Button
        type="primary"
        size="small"
        icon={<CreditCardOutlined />}
        loading={payingId === row.id}
        disabled={payingId !== null && payingId !== row.id}
        onClick={() => handlePay(row.id as string)}
        aria-label={t('paymentSchedule.payNowFor', { defaultValue: 'Pay {{name}} now', name: row.name })}
      >
        {t('paymentSchedule.payNow', { defaultValue: 'Pay now' })}
      </Button>
    ) : null;
  const renderDate = (value: string | null) =>
    formatCalendarDate(value, i18n.language) ?? (
      <Typography.Text type="secondary">{t('paymentSchedule.noDate', { defaultValue: 'Date not set yet' })}</Typography.Text>
    );

  const totalLine =
    total !== null && currency ? (
      <Flex justify="space-between" align="center" style={{ paddingTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: 4 }}>
        <Typography.Text strong>{t('paymentSchedule.total', { defaultValue: 'Total' })}</Typography.Text>
        <Typography.Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMinor(total, currency, i18n.language)}
        </Typography.Text>
      </Flex>
    ) : null;

  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      {isMobile ? (
        <List
          dataSource={rows}
          split
          renderItem={row => (
            <List.Item key={row.key} style={{ paddingInline: 0 }}>
              <Flex vertical gap={4} style={{ width: '100%' }}>
                <Flex justify="space-between" align="flex-start" gap={8}>
                  <Typography.Text strong style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{row.name}</Typography.Text>
                  {renderStatus(row.status)}
                </Flex>
                <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    <CalendarOutlined style={{ marginInlineEnd: 4 }} />
                    {renderDate(row.due_date)}
                  </Typography.Text>
                  {renderAmount(row)}
                </Flex>
                {row.pay_online && <Flex justify="flex-end">{renderPay(row)}</Flex>}
              </Flex>
            </List.Item>
          )}
        />
      ) : (
        <Table<ClientPaymentInstallment & { key: string }>
          dataSource={rows}
          pagination={false}
          size="small"
          columns={[
            { key: 'name', dataIndex: 'name', title: t('paymentSchedule.columns.name', { defaultValue: 'Payment' }) },
            {
              key: 'amount',
              title: t('paymentSchedule.columns.amount', { defaultValue: 'Amount' }),
              align: 'right',
              width: 160,
              render: (_value: unknown, row) => renderAmount(row),
            },
            {
              key: 'due_date',
              dataIndex: 'due_date',
              title: t('paymentSchedule.columns.dueDate', { defaultValue: 'Due date' }),
              width: 160,
              render: (value: string | null) => renderDate(value),
            },
            {
              key: 'status',
              dataIndex: 'status',
              title: t('paymentSchedule.columns.status', { defaultValue: 'Status' }),
              width: 130,
              render: (value: ClientInstallmentStatus) => renderStatus(value),
            },
            ...(schedule.online_payments
              ? [
                  {
                    key: 'pay',
                    title: t('paymentSchedule.columns.pay', { defaultValue: 'Pay' }),
                    width: 130,
                    align: 'right' as const,
                    render: (_value: unknown, row: ClientPaymentInstallment) => renderPay(row),
                  },
                ]
              : []),
          ]}
        />
      )}
      {totalLine}
    </Card>
  );
};

const STATUS_COLOR: Record<ClientInstallmentStatus, string> = {
  upcoming: 'default',
  due: 'processing',
  overdue: 'error',
  partly_paid: 'warning',
  paid: 'success',
};

export default PaymentScheduleCard;
